import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [formData, setFormData] = useState({
        email: "",
        password: ""
    });
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.email || !formData.password) {
            toast.error("Please fill in all fields");
            return;
        }

        setIsLoading(true);

        try {
            await login(formData.email, formData.password);
            navigate("/");
        } catch (error) {
            toast.error("Invalid email or password");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="hsbc-login-page">
            {/* ── Top header bar ── */}
            <header className="hsbc-header">
                <div className="hsbc-header-inner">
                    <div className="hsbc-logo-group">
                        {/* Hexagon logo mark */}
                        <svg className="hsbc-logo-mark" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polygon points="20,2 37,11 37,29 20,38 3,29 3,11" fill="#DB0011" />
                            <polygon points="20,8 31,14 31,26 20,32 9,26 9,14" fill="white" />
                            <polygon points="20,13 26,16.5 26,23.5 20,27 14,23.5 14,16.5" fill="#DB0011" />
                        </svg>
                        <span className="hsbc-logo-name">SimpleCRM</span>
                    </div>
                </div>
            </header>

            {/* ── Red accent stripe ── */}
            <div className="hsbc-red-stripe" />

            {/* ── Main content ── */}
            <main className="hsbc-main">
                <div className="hsbc-main-inner">
                    {/* Left — welcome messaging */}
                    <div className="hsbc-welcome">
                        <h1 className="hsbc-welcome-title">
                            Welcome to<br />SimpleCRM Portal
                        </h1>
                        <p className="hsbc-welcome-desc">
                            Access your dashboard, manage leads, and monitor your team's performance — securely from anywhere.
                        </p>

                        <div className="hsbc-features">
                            <div className="hsbc-feature">
                                <div className="hsbc-feature-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                </div>
                                <div>
                                    <h3>Enterprise Security</h3>
                                    <p>Bank-level encryption protects your data at every step</p>
                                </div>
                            </div>
                            <div className="hsbc-feature">
                                <div className="hsbc-feature-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                                </div>
                                <div>
                                    <h3>Team Management</h3>
                                    <p>Collaborate with your team in real-time</p>
                                </div>
                            </div>
                            <div className="hsbc-feature">
                                <div className="hsbc-feature-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
                                </div>
                                <div>
                                    <h3>Live Analytics</h3>
                                    <p>Real-time dashboards and reporting tools</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right — login card */}
                    <div className="hsbc-login-card">
                        <h2 className="hsbc-card-title">Log on</h2>
                        <p className="hsbc-card-subtitle">Enter your credentials to access your account</p>

                        <form onSubmit={handleSubmit} className="hsbc-form">
                            <div className="hsbc-form-group">
                                <label htmlFor="email" className="hsbc-label">
                                    Username / Email
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                                    placeholder="Enter your email"
                                    className="hsbc-input"
                                    required
                                    autoComplete="email"
                                />
                            </div>

                            <div className="hsbc-form-group">
                                <label htmlFor="password" className="hsbc-label">
                                    Password
                                </label>
                                <div className="hsbc-input-pw-wrap">
                                    <input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        value={formData.password}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                                        placeholder="Enter your password"
                                        className="hsbc-input"
                                        required
                                        autoComplete="current-password"
                                    />
                                    <button
                                        type="button"
                                        className="hsbc-pw-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                        tabIndex={-1}
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="hsbc-submit"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <span className="hsbc-loading">
                                        <span className="hsbc-spinner" />
                                        Logging in…
                                    </span>
                                ) : (
                                    "Log on"
                                )}
                            </button>
                        </form>

                        <div className="hsbc-security-note">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="hsbc-shield-icon"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                            <span>Your connection is encrypted and secure. We will never ask for your full password by email or phone.</span>
                        </div>
                    </div>
                </div>
            </main>

            {/* ── Footer ── */}
            <footer className="hsbc-footer">
                <div className="hsbc-footer-inner">
                    <div className="hsbc-footer-links">
                        <span>Privacy & Cookies</span>
                        <span className="hsbc-footer-dot">·</span>
                        <span>Terms & Conditions</span>
                        <span className="hsbc-footer-dot">·</span>
                        <span>Security</span>
                        <span className="hsbc-footer-dot">·</span>
                        <span>Accessibility</span>
                    </div>
                    <p className="hsbc-footer-copy">&copy; 2026 SimpleCRM. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
}
